import { MutationType, STPageType } from '@tencent/slide-sdk-pb-model';
import { assert } from 'ts-essentials';
import { shallowOmit } from '../../utils/object-utils';
import { BaseMutation } from '../base/base-mutation';
import { Rule } from '../base/mutation-rules';
import { invert } from '../../utils/protoutil';
import type { DeltaOf } from '../../utils/delta';
import type {
    ILayoutData,
    ILayoutDataDelta,
    IMasterData,
    IMasterDataDelta,
    INoteSlideData,
    INotesMasterData,
    ISlideData,
    ISlideDataDelta,
} from '../../data';
import type { DeepReadonly } from 'ts-essentials';
import type {
    IPageProperties,
    ISlideProperties,
    ISlideLayoutProperties,
    ISlideMasterProperties,
    INotesProperties,
    INotesMasterProperties,
} from '@tencent/slide-sdk-pb-model';
import type { PresentationDataModel } from '../../model/presentation-data-model';
import type { MaybeError } from '../base/base-mutation';
import type { IValidSetPagePropertiesMutationData } from '../base/valid-mutation-data';

export class SetPagePropertiesMutation extends BaseMutation<
    PresentationDataModel,
    IValidSetPagePropertiesMutationData
> {
    public readonly type: MutationType = MutationType.SetPageProperties;

    public apply(presentation: PresentationDataModel) {
        const { pageId, pageType } = this.data;
        if (pageType === STPageType.slide) {
            const slide = presentation.store.getSlideById(pageId);
            assert(slide, 'slide should exist');
            slide.store.updateProps(PageDeltaUtils.toSlideDataDelta(this.data));
            return;
        }

        if (pageType === STPageType.slideLayout) {
            const layout = presentation.store.getLayoutById(pageId);
            assert(layout, 'layout should exist');
            layout.store.patchData(PageDeltaUtils.toLayoutDataDelta(this.data), { slideGuideList: true });
            return;
        }

        if (pageType === STPageType.slideMaster) {
            const master = presentation.store.getMasterById(pageId);
            assert(master, 'master should exist');
            master.store.patchData(PageDeltaUtils.toMasterDataDelta(this.data), { slideGuideList: true });
            return;
        }

        if (pageType === STPageType.notes) {
            const noteSlide = presentation.store.getNoteSlideById(pageId);
            assert(noteSlide, 'noteSlide should exist');
            noteSlide.store.patchData(PageDeltaUtils.toNoteSlideDataDelta(this.data));
            return;
        }

        if (pageType === STPageType.notesMaster) {
            const notesMaster = presentation.store.getNotesMasterById(pageId);
            assert(notesMaster, 'notesMaster should exist');
            notesMaster.store.patchData(PageDeltaUtils.toNotesMasterDataDelta(this.data));
            return;
        }
    }

    public invert(presentation: PresentationDataModel): [IValidSetPagePropertiesMutationData] | [] {
        const { pageId, pageType } = this.data;
        const setPageProperties = { type: MutationType.SetPageProperties, pageId, pageType };
        if (pageType === STPageType.slide) {
            const slide = presentation.store.getSlideById(pageId);
            assert(slide, 'slide should exist');

            return [
                {
                    ...setPageProperties,
                    ...invert(
                        PageDeltaUtils.fromSlideData(slide.store.data),
                        PageDeltaUtils.fromMutationData(this.data),
                    ),
                },
            ];
        }

        if (pageType === STPageType.slideLayout) {
            const layout = presentation.store.getLayoutById(pageId);
            assert(layout, 'layout should exist');

            return [
                {
                    ...setPageProperties,
                    ...invert(
                        PageDeltaUtils.fromLayoutData(layout.store.data),
                        PageDeltaUtils.fromMutationData(this.data),
                        { layoutProperties: { slideGuideList: true } },
                    ),
                },
            ];
        }

        if (pageType === STPageType.slideMaster) {
            const master = presentation.store.getMasterById(pageId);
            assert(master, 'master should exist');

            return [
                {
                    ...setPageProperties,
                    ...invert(
                        PageDeltaUtils.fromMasterData(master.store.data),
                        PageDeltaUtils.fromMutationData(this.data),
                        { masterProperties: { slideGuideList: true } },
                    ),
                },
            ];
        }

        if (pageType === STPageType.notes) {
            const noteSlide = presentation.store.getNoteSlideById(pageId);
            assert(noteSlide, 'noteSlide should exist');

            return [
                {
                    ...setPageProperties,
                    ...invert(
                        PageDeltaUtils.fromNoteSlideData(noteSlide.store.data),
                        PageDeltaUtils.fromMutationData(this.data),
                    ),
                },
            ];
        }

        if (pageType === STPageType.notesMaster) {
            const notesMaster = presentation.store.getNotesMasterById(pageId);
            assert(notesMaster, 'notesMaster should exist');

            return [
                {
                    ...setPageProperties,
                    ...invert(
                        PageDeltaUtils.fromNotesMasterData(notesMaster.store.data),
                        PageDeltaUtils.fromMutationData(this.data),
                    ),
                },
            ];
        }

        return [];
    }

    public check(presentation: PresentationDataModel): MaybeError {
        const { pageId, pageType } = this.data;
        return Rule.page.should.exist(presentation, pageId, pageType);
    }
}

type IPageDelta = {
    pageProperties?: IPageProperties;
    slideProperties?: ISlideProperties;
    layoutProperties?: ISlideLayoutProperties;
    masterProperties?: ISlideMasterProperties;
    notesProperties?: INotesProperties;
    notesMasterProperties?: INotesMasterProperties;
};
type ICSld = Omit<IPageProperties, 'transition'>;

export class PageDeltaUtils {
    static fromMutationData(
        data: DeepReadonly<Omit<IValidSetPagePropertiesMutationData, 'type'>>,
    ): DeepReadonly<IPageDelta> {
        return shallowOmit(data, 'type', 'pageId', 'pageType');
    }

    static fromSlideData(data: DeepReadonly<ISlideData>): DeepReadonly<IPageDelta> {
        return {
            pageProperties: {
                ...shallowOmit(data.cSld, 'spTree'),
                transition: data.transition,
            },
            slideProperties: {
                layoutId: data.layoutId,
                show: data.show,
                clrMapOvr: data.clrMapOvr,
                showMasterSp: data.showMasterSp,
            },
        };
    }

    static fromLayoutData(data: DeepReadonly<ILayoutData>): DeepReadonly<IPageDelta> {
        return {
            pageProperties: {
                ...shallowOmit(data.cSld, 'spTree'),
                transition: data.transition,
            },
            layoutProperties: {
                slideMasterId: data.masterId,
                hf: data.hf,
                layoutType: data.layoutType,
                clrMapOvr: data.clrMapOvr,
                showMasterSp: data.showMasterSp,
                slideGuideList: data.slideGuideList && { guide: data.slideGuideList },
            },
        };
    }

    static fromMasterData(data: DeepReadonly<IMasterData>): DeepReadonly<IPageDelta> {
        return {
            pageProperties: {
                ...shallowOmit(data.cSld, 'spTree'),
                transition: data.transition,
            },
            masterProperties: {
                hf: data.hf,
                themeId: data.themeId,
                clrMap: data.clrMap,
                txStyles: data.txStyles,
                slideGuideList: data.slideGuideList && { guide: data.slideGuideList },
            },
        };
    }

    static fromNoteSlideData(data: DeepReadonly<INoteSlideData>): DeepReadonly<IPageDelta> {
        return {
            pageProperties: {
                ...shallowOmit(data.cSld, 'spTree'),
                transition: data.transition,
            },
            notesProperties: {},
        };
    }

    static fromNotesMasterData(data: DeepReadonly<INotesMasterData>): DeepReadonly<IPageDelta> {
        return {
            pageProperties: {
                ...shallowOmit(data.cSld, 'spTree'),
                transition: data.transition,
            },
            notesMasterProperties: {
                notesStyle: data.notesStyle,
                hf: data.hf,
                clrMap: data.clrMap,
            },
        };
    }

    static toSlideDataDelta(delta: IPageDelta): DeltaOf<ISlideData> {
        const { pageProperties, slideProperties = {} } = delta;
        const { layoutId, show, clrMapOvr, showMasterSp } = slideProperties;
        const { cSld, transition } = this.separatePageProperties(pageProperties);
        return { layoutId, show, clrMapOvr, showMasterSp, cSld, transition };
    }

    static toLayoutDataDelta(delta: IPageDelta): DeltaOf<ILayoutData> {
        const { pageProperties, layoutProperties = {} } = delta;
        const { slideMasterId, hf, layoutType, clrMapOvr, showMasterSp, slideGuideList } = layoutProperties;
        const { cSld, transition } = this.separatePageProperties(pageProperties);
        return {
            cSld,
            masterId: slideMasterId,
            hf,
            layoutType,
            clrMapOvr,
            showMasterSp,
            slideGuideList: slideGuideList?.guide,
            transition,
        };
    }

    static toMasterDataDelta(delta: IPageDelta): DeltaOf<IMasterData> {
        const { pageProperties, masterProperties = {} } = delta;
        const { hf, themeId, clrMap, txStyles, slideGuideList } = masterProperties;
        const { cSld, transition } = this.separatePageProperties(pageProperties);
        return {
            cSld,
            hf,
            themeId,
            clrMap,
            txStyles,
            slideGuideList: slideGuideList?.guide,
            transition,
        };
    }

    static toNoteSlideDataDelta(delta: IPageDelta): DeltaOf<INoteSlideData> {
        const { pageProperties } = delta;
        const { cSld, transition } = this.separatePageProperties(pageProperties);
        return { cSld, transition };
    }

    static toNotesMasterDataDelta(delta: IPageDelta): DeltaOf<INotesMasterData> {
        const { pageProperties, notesMasterProperties = {} } = delta;
        const { notesStyle, hf, clrMap } = notesMasterProperties;
        const { cSld, transition } = this.separatePageProperties(pageProperties);
        return { cSld, notesStyle, hf, clrMap, transition };
    }

    static fromSlideDataDelta(delta: ISlideDataDelta): IPageDelta {
        const { cSld, transition, showMasterSp, show, clrMapOvr, layoutId } = delta;
        return {
            pageProperties: {
                ...cSld,
                transition,
            },
            slideProperties: {
                layoutId,
                showMasterSp,
                show,
                clrMapOvr,
            },
        };
    }

    static fromLayoutDataDelta(delta: ILayoutDataDelta): IPageDelta {
        const { cSld, transition, showMasterSp, clrMapOvr, hf } = delta;
        return {
            pageProperties: {
                ...cSld,
                transition,
            },
            layoutProperties: {
                showMasterSp,
                clrMapOvr,
                hf,
            },
        };
    }

    static fromMasterDataDelta(delta: IMasterDataDelta): IPageDelta {
        const { cSld, transition, clrMap, hf, txStyles, themeId } = delta;
        return {
            pageProperties: {
                ...cSld,
                transition,
            },
            masterProperties: {
                clrMap,
                hf,
                txStyles,
                themeId,
            },
        };
    }

    // 将pageProperties中的属性分离开来，用于赋值给sdk-data
    static separatePageProperties(pageProperties?: IPageProperties) {
        return {
            cSld: pageProperties ? shallowOmit(pageProperties, 'transition') : pageProperties,
            transition: pageProperties?.transition,
        };
    }

    // 通过sdk-data的数据，聚合成pageProperties
    static generatePagePropertiesFromData(cSld?: ICSld, transition?: IPageProperties['transition']): IPageProperties {
        return {
            ...cSld,
            transition,
        };
    }
}
